import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const PO_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total PO",
    titleColor: "#127aea",
    valueColor: "#2c7fd8",
    filterValue: null,
  },
  {
    key: "draftCount",
    label: "Draft",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterValue: "Draft",
  },
  {
    key: "waitingForApprovalCount",
    label: "Waiting for Approval",
    titleColor: "#7D6608",
    valueColor: "#D4AC0D",
    filterValue: "Waiting for approval",
  },
  {
    key: "approvedCount",
    label: "Approved",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "Approved",
  },
  {
    key: "giPartiallyCreatedCount",
    label: "GI Partially Created",
    titleColor: "#9C640C",
    valueColor: "#F39C12",
    filterValue: "GI Partially Created",
    isGiStatus: true,
  },
  {
    key: "giCreatedCount",
    label: "GI Created",
    titleColor: "#1B4F72",
    valueColor: "#2E86C1",
    filterValue: "GI Created",
    isGiStatus: true,
  },
  {
    key: "activeMaterialsGrossBasicPrice",
    label: "Gross Basic Price",
    titleColor: "#1A5276",
    valueColor: "#2874A6",
    filterValue: null,
    isAmount: true,
  },
];

const PoStatCard = ({
  label,
  value,
  fullValue,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable ${
        isActive ? "procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      title={fullValue || `Filter by ${label}`}
    >
      <div className="card-body">
        <div
          className="stat-title"
          style={{
            color: titleColor,
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {label}
        </div>

        <div
          className="stat-value"
          style={{
            color: valueColor,
            fontSize: "20px",
            fontWeight: "700",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  </div>
);

const PoStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" />
        <div className="stat-skeleton stat-skeleton-value" />
      </div>
    </div>
  </div>
);

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractPosPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { pos: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      pos: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list = dataBlock.pos || dataBlock.poList || dataBlock.items || root.pos;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.pos || inner.poList || inner.items;
    }
  }

  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.pos == null &&
    dataBlock.poList == null &&
    dataBlock.items == null &&
    (dataBlock._id != null || dataBlock.poNumber != null)
  ) {
    list = [dataBlock];
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  return {
    pos: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
  };
}

function formatPoDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const dateOnlyMatch = str.match(/^(\d{2}-\d{2}-\d{4})/);
  if (dateOnlyMatch) return dateOnlyMatch[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString();
}

function poNumber(po) {
  return po?.poNumber || po?.poNo || "-";
}

function poSummaryValue(po) {
  const s = po?.poSummary || po?.summary || "";
  return s.trim() ? s : "-";
}

function poSupplierNameValue(po) {
  if (!po || typeof po !== "object") return "-";
  const direct = String(po.supplierName || "").trim();
  if (direct) return direct;

  const contacts = Array.isArray(po.supplierContacts)
    ? po.supplierContacts
    : [];
  if (contacts.length > 0) {
    const fromContact = String(
      (contacts[0] && contacts[0].supplierName) || "",
    ).trim();
    if (fromContact) return fromContact;
  }

  if (po.supplier && typeof po.supplier === "object") {
    const fromSupplier = String(
      po.supplier.supplierName ||
        po.supplier.clientname ||
        po.supplier.name ||
        "",
    ).trim();
    if (fromSupplier) return fromSupplier;
  }

  return "-";
}

function poGrossBasicPriceValue(po) {
  if (po?.grossbasicPrice != null && po.grossbasicPrice !== "") {
    return po.grossbasicPrice;
  }
  if (po?.grossBasicPrice != null && po.grossBasicPrice !== "") {
    return po.grossBasicPrice;
  }
  if (po?.grossTotalBasicPrice != null && po.grossTotalBasicPrice !== "") {
    return po.grossTotalBasicPrice;
  }
  const materials = Array.isArray(po?.materials) ? po.materials : [];
  if (materials.length === 0) return null;
  const total = materials.reduce((acc, material) => {
    if (material?.grossbasicPrice != null && material.grossbasicPrice !== "") {
      return acc + (parseFloat(material.grossbasicPrice) || 0);
    }
    return (
      acc +
      (parseFloat(material?.basicPrice) || 0) *
        (parseFloat(material?.quantity) || 0)
    );
  }, 0);
  return total > 0 ? total : null;
}

function formatPoGrossBasicPrice(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Compact currency for equal-width stat cards; full amount stays in tooltip. */
function formatPoGrossBasicPriceCompact(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";

  const abs = Math.abs(num);
  if (abs >= 10000000) {
    return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  }
  if (abs >= 100000) {
    return `₹ ${(num / 100000).toFixed(2)} L`;
  }
  return `₹ ${formatPoGrossBasicPrice(num)}`;
}

function formatPoStatCardValue(card, rawValue) {
  if (card.isAmount) {
    const compact = formatPoGrossBasicPriceCompact(rawValue);
    const full =
      formatPoGrossBasicPrice(rawValue) === "-"
        ? "₹ 0.00"
        : `₹ ${formatPoGrossBasicPrice(rawValue)}`;
    return { display: compact === "-" ? "₹ 0.00" : compact, full };
  }
  return { display: rawValue ?? 0, full: null };
}

function poCurrencySymbol(currency) {
  const code = String(currency || "INR")
    .trim()
    .toUpperCase();
  if (code === "USD") return "$";
  if (code === "EUR") return "€";
  return "₹";
}

function renderPoGrossBasicPrice(po) {
  const amount = formatPoGrossBasicPrice(poGrossBasicPriceValue(po));
  if (amount === "-") return "-";
  return `${poCurrencySymbol(po?.poCurrency)} ${amount}`;
}

function poCreatedByName(po) {
  const createdBy = po?.createdBy;
  if (!createdBy) return po?.ownerName || "-";
  if (typeof createdBy === "string") return createdBy.trim() || "-";
  return createdBy.name || createdBy.userName || po?.ownerName || "-";
}

function isCurrentUserApprover(po) {
  const userId = String(localStorage.getItem("id") || "").trim();
  const approverId = String(po?.approverId || "").trim();
  return Boolean(userId && approverId && userId === approverId);
}

function isPoApprovedStatus(po) {
  return (
    String(po?.poStatus || "")
      .trim()
      .toLowerCase() === "approved"
  );
}

function canCurrentUserApprovePo(po) {
  if (!isCurrentUserApprover(po)) return false;
  return (
    String(po?.poStatus || "")
      .trim()
      .toLowerCase() === "waiting for approval"
  );
}

function PoApproveToggle({ checked, disabled, onToggle, loading }) {
  return (
    <button
      type="button"
      className="po-approve-toggle"
      onClick={(e) => {
        e.preventDefault();
        if (disabled || loading || checked) return;
        onToggle?.();
      }}
      disabled={disabled || loading}
      title={
        checked
          ? "Approved"
          : disabled
            ? "Approval not available"
            : "Click to approve this PO"
      }
      aria-pressed={checked}
      style={{
        position: "relative",
        width: "48px",
        height: "20px",
        border: "none",
        borderRadius: "999px",
        padding: 0,
        cursor: disabled || checked ? "default" : "pointer",
        background: checked
          ? "linear-gradient(135deg, #20c997 0%, #1199B2 100%)"
          : "linear-gradient(135deg, #d7dee5 0%, #c5CED6 100%)",
        boxShadow: checked
          ? "inset 0 0 0 1px rgba(17,153,178,0.25)"
          : "inset 0 0 0 1px rgba(0,0,0,0.06)",
        transition: "background 0.25s ease, box-shadow 0.25s ease",
        opacity: disabled && !checked ? 0.7 : 1,
        outline: "none",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: checked ? "28px" : "2px",
          transform: "translateY(-50%)",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          backgroundColor: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
          transition: "left 0.25s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "8px",
          color: checked ? "#1199B2" : "#8a97a5",
        }}
      >
        {loading ? (
          <PulseLoader color="#1199B2" size={2} />
        ) : (
          <i
            className={`fa-solid ${checked ? "fa-check" : "fa-minus"}`}
            aria-hidden
          />
        )}
      </span>
      <span
        style={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          left: checked ? "5px" : "20px",
          fontSize: "8px",
          fontWeight: 700,
          letterSpacing: "0.2px",
          color: checked ? "#fff" : "#5f6b76",
          textTransform: "uppercase",
          pointerEvents: "none",
        }}
      >
        {checked ? "Yes" : "No"}
      </span>
    </button>
  );
}

function renderApprovedToggleCell(po, { onApprove, isApproving, approvePoId }) {
  if (!isCurrentUserApprover(po)) {
    return <span className="text-muted">-</span>;
  }

  const approved = isPoApprovedStatus(po);
  const canApprove = canCurrentUserApprovePo(po);
  const pid = getPoId(po);
  const loading = isApproving && approvePoId === pid;

  if (!approved && !canApprove) {
    return <span className="text-muted">-</span>;
  }

  return (
    <PoApproveToggle
      checked={approved}
      disabled={!canApprove || approved}
      loading={loading}
      onToggle={onApprove}
    />
  );
}

function normalizePoUsers(users) {
  if (!Array.isArray(users)) return [];
  return users
    .map((user) => ({
      _id: String(user._id || user.id || "").trim(),
      name: String(user.name || user.userName || user.email || "").trim(),
    }))
    .filter((user) => user._id && user.name);
}

const PO_STATUS_OPTIONS = [
  { value: "Draft", label: "Draft" },
  { value: "Waiting for approval", label: "Waiting for approval" },
  { value: "Approved", label: "Approved" },
];

const GI_STATUS_OPTIONS = [
  { value: "GI In Progress", label: "GI In Progress" },
  { value: "GI Partially Created", label: "GI Partially Created" },
  { value: "GI Created", label: "GI Created" },
];

function getPoId(po) {
  return po?._id || po?.id || "";
}

function getPoPdfLink(po) {
  return String(po?.pdfLink || po?.pdfDownloadUrl || "").trim();
}

function hasPoPdf(po) {
  return Boolean(getPoPdfLink(po));
}

async function resolvePoPdfDownloadUrl(po) {
  const signedUrl = String(po?.pdfDownloadUrl || "").trim();
  if (signedUrl) return signedUrl;

  const pid = getPoId(po);
  if (!pid) {
    throw new Error("PO ID is missing");
  }

  const response = await axios.post(API_URL, {
    action: "get-po-detail-by-id",
    poId: pid,
  });
  const responseData = normalizeApiPayload(parseApiResponse(response.data));

  if (!isApiSuccess(responseData) || !responseData?.data) {
    throw new Error(responseData?.message || "Failed to fetch PO PDF URL");
  }

  const downloadUrl = String(
    responseData.data.pdfDownloadUrl || responseData.data.pdfLink || "",
  ).trim();

  if (!downloadUrl) {
    throw new Error("PDF is not available for this PO");
  }

  return downloadUrl;
}

function getPoPdfFilename(po) {
  const number = poNumber(po);
  const base = number && number !== "-" ? number : "PO";
  return /\.pdf$/i.test(base) ? base : `${base}.pdf`;
}

function getPoDisplayStatus(po) {
  const poStatus = String(po?.poStatus || "").trim();
  const giStatus = String(po?.giStatus || "").trim();

  if (!poStatus) return "-";
  if (giStatus) {
    return `${poStatus}, ${giStatus}`;
  }
  return poStatus;
}

const PO_SORT_COLUMN_MAP = {
  poNumber: "poNumber",
  poDate: "poDate",
  poDeliveryDate: "poDeliveryDate",
  poStatus: "poStatus",
  createdBy: "createdBy",
};

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";


const DEFAULT_FILTER_CHECKS = {
  poStatus: false,
  giStatus: false,
  createdBy: false,
  approverId: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  poStatus: "",
  giStatus: "",
  createdBy: "",
  approverId: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  poStatus: "",
  giStatus: "",
  createdBy: "",
  approverId: "",
  fromDate: "",
  toDate: "",
};
const MAX_ACTIVE_FILTERS = 2; // change to however many filters can be active at once

const Po = () => {
  const {
    canCreate: canCreatePo,
    canView: canViewPo,
    canUpdate: canUpdatePo,
    canDelete: canDeletePo,
  } = useModuleAccess("po");
  const navigate = useNavigate();

  const savedFilters = JSON.parse(sessionStorage.getItem("poFilters") || "{}");
  const [pos, setPos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [deletePoId, setDeletePoId] = useState(null);
  const [deletePoNumber, setDeletePoNumber] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [revertPoId, setRevertPoId] = useState(null);
  const [revertPoNumber, setRevertPoNumber] = useState("");
  const [isReverting, setIsReverting] = useState(false);
  const [downloadingPoId, setDownloadingPoId] = useState("");
  const [approvePoId, setApprovePoId] = useState(null);
  const [approvePoNumber, setApprovePoNumber] = useState("");
  const [pendingApproveIds, setPendingApproveIds] = useState([]);
  const [isApproving, setIsApproving] = useState(false);
  const [selectedPoIds, setSelectedPoIds] = useState(() => new Set());

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState(
    savedFilters.filterChecks || DEFAULT_FILTER_CHECKS,
  );
  const [filterInputs, setFilterInputs] = useState(
    savedFilters.filterInputs || DEFAULT_FILTER_INPUTS,
  );
  const [activeMultiFilters, setActiveMultiFilters] = useState(
    savedFilters.activeMultiFilters || DEFAULT_ACTIVE_MULTI_FILTERS,
  );
  const filterPanelRef = useRef(null);

  const [poUsers, setPoUsers] = useState([]);
  const [isPoUsersLoading, setIsPoUsersLoading] = useState(false);
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const [isPoStatusLoading, setIsPoStatusLoading] = useState(true);
  const [poStatus, setPoStatus] = useState({
    totalCount: 0,
    draftCount: 0,
    waitingForApprovalCount: 0,
    approvedCount: 0,
    giPartiallyCreatedCount: 0,
    giCreatedCount: 0,
    activeMaterialsGrossBasicPrice: 0,
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("all");
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const anyMultiFilterActive =
    !!activeMultiFilters.poStatus ||
    !!activeMultiFilters.giStatus ||
    !!activeMultiFilters.createdBy ||
    !!activeMultiFilters.approverId ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toApiDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}-${mm}-${yyyy}`;
  }

  function extractExcelBase64(raw) {
    if (raw == null) return null;
    if (typeof raw === "string") {
      const t = raw.trim();
      if (t.startsWith("UEs")) return raw;
      if (t.startsWith("{")) {
        try {
          return extractExcelBase64(JSON.parse(t));
        } catch {
          return null;
        }
      }
      return null;
    }
    if (typeof raw === "object") {
      if (raw.body != null) return extractExcelBase64(raw.body);
      if (typeof raw.data === "string") return extractExcelBase64(raw.data);
      if (raw.data != null && typeof raw.data === "object") {
        const nested = extractExcelBase64(raw.data);
        if (nested) return nested;
      }
      if (raw.file != null) return extractExcelBase64(raw.file);
      if (raw.base64 != null) return extractExcelBase64(raw.base64);
    }
    return null;
  }

  function triggerXlsxDownloadFromBase64(base64, filenamePrefix = "RFQs") {
    if (!base64 || typeof base64 !== "string" || !base64.startsWith("UEs")) {
      throw new Error("Invalid Excel data received");
    }
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    link.download = `${filenamePrefix}-${timestamp}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  const getCurrentUserId = () => localStorage.getItem("id") || undefined;

  const fetchPos = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-po",
        page: currentPage,
        limit: itemsPerPage,
        includeInactive: false,
        currentUserId: getCurrentUserId(),
        userid: "",
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (activeMultiFilters.giStatus) {
        payload.poStatus = ["Approved"];
        payload.giStatus = activeMultiFilters.giStatus;
      } else if (activeMultiFilters.poStatus) {
        payload.poStatus = [activeMultiFilters.poStatus];
        if (activeMultiFilters.poStatus === "Approved") {
          payload.excludeStartedGi = true;
        }
      }

      if (activeMultiFilters.createdBy) {
        payload.userid = activeMultiFilters.createdBy;
        payload.createdBy = activeMultiFilters.createdBy;
      }
      if (activeMultiFilters.approverId) {
        payload.approverId = activeMultiFilters.approverId;
      }
      if (activeMultiFilters.fromDate) {
        payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }

      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const { pos: list, pagination } = extractPosPayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setPos(list);
        const total =
          typeof pagination.total === "number"
            ? pagination.total
            : typeof pagination.totalCount === "number"
              ? pagination.totalCount
              : list.length;
        setTotalCount(total);
      } else {
        toast.error(
          responseData?.message || "Failed to fetch purchase orders",
          {
            autoClose: 2000,
          },
        );
        setPos([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch PO error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        typeof errData === "object" && errData?.message
          ? errData.message
          : "An error occurred while fetching purchase orders";
      toast.error(msg, { autoClose: 2000 });
      setPos([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activeMultiFilters,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchPos();
  }, [fetchPos]);

  useEffect(() => {
    let cancelled = false;

    const fetchPoUsers = async () => {
      setIsPoUsersLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-po-users",
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (cancelled) return;

        if (isApiSuccess(responseData) && Array.isArray(responseData.data)) {
          setPoUsers(normalizePoUsers(responseData.data));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch PO users error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsPoUsersLoading(false);
        }
      }
    };

    fetchPoUsers();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!window.$) return undefined;

    const $modal = window.$("#modaldelete");
    const onHidden = () => {
      setDeletePoId(null);
      setDeletePoNumber("");
    };

    $modal.on("hidden.bs.modal", onHidden);
    return () => {
      $modal.off("hidden.bs.modal", onHidden);
    };
  }, []);

  useEffect(() => {
    if (!window.$) return undefined;

    const $modal = window.$("#modalrevertpo");
    const onHidden = () => {
      setRevertPoId(null);
      setRevertPoNumber("");
    };

    $modal.on("hidden.bs.modal", onHidden);
    return () => {
      $modal.off("hidden.bs.modal", onHidden);
    };
  }, []);

  useEffect(() => {
    if (!window.$) return undefined;

    const $modal = window.$("#modalapprove");
    const onHidden = () => {
      setApprovePoId(null);
      setApprovePoNumber("");
      setPendingApproveIds([]);
    };

    $modal.on("hidden.bs.modal", onHidden);
    return () => {
      $modal.off("hidden.bs.modal", onHidden);
    };
  }, []);

  useEffect(() => {
    setSelectedPoIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      pos.forEach((po) => {
        const id = getPoId(po);
        if (!id || !next.has(id)) return;
        if (!canCurrentUserApprovePo(po)) next.delete(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [pos]);

const fetchPoStatusCount = useCallback(async () => {
  setIsPoStatusLoading(true);
  try {
    const payload = { action: "po-count", userid: "" };

    // Reflect the search box in the stat cards too
    const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
    if (trimmedSearch) {
      payload.search = trimmedSearch;
    }

    if (activeMultiFilters.createdBy) {
      payload.userid = activeMultiFilters.createdBy;
    }
    if (activeMultiFilters.approverId) {
      payload.approverId = activeMultiFilters.approverId;
    }
    if (activeMultiFilters.fromDate) {
      payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
    }
    if (activeMultiFilters.toDate) {
      payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
    }

    // PO Status / GI Status are mutually exclusive - same logic as fetchPos
    if (activeMultiFilters.giStatus) {
      payload.poStatus = ["Approved"];
      payload.giStatus = activeMultiFilters.giStatus;
    } else if (activeMultiFilters.poStatus) {
      payload.poStatus = [activeMultiFilters.poStatus];
      if (activeMultiFilters.poStatus === "Approved") {
        payload.excludeStartedGi = true;
      }
    }

    const response = await axios.post(API_URL, payload);
    const responseData = normalizeApiPayload(parseApiResponse(response.data));

    if (isApiSuccess(responseData) && responseData.data) {
      setPoStatus(responseData.data);
    }
  } catch (error) {
    console.error("PO status count error:", error);
  } finally {
    setIsPoStatusLoading(false);
  }
}, [activeMultiFilters, activeSearchQuery]);

  useEffect(() => {
    fetchPoStatusCount();
  }, [fetchPoStatusCount]);

  const handleExportPo = async () => {
    if (exportMode === "date") {
      if (!exportFromDate || !exportToDate) {
        toast.error("Please select both from date and to date");
        return;
      }

      if (new Date(exportFromDate) > new Date(exportToDate)) {
        toast.error("From date cannot be greater than to date");
        return;
      }
    }

    setIsExporting(true);

    try {
      const payload = {
        action: "po-excel",
      };

      if (exportMode === "date") {
        payload.fromDate = toApiDDMMYYYY(exportFromDate);
        payload.toDate = toApiDDMMYYYY(exportToDate);
      }
      if (activeSearchQuery?.trim()) {
        payload.search = activeSearchQuery.trim();
      }

      if (activeMultiFilters.giStatus) {
        payload.poStatus = ["Approved"];
        payload.giStatus = activeMultiFilters.giStatus;
      } else if (activeMultiFilters.poStatus) {
        payload.poStatus = activeMultiFilters.poStatus;
      }

      if (activeMultiFilters.createdBy) {
        payload.userid = activeMultiFilters.createdBy;
      }
      if (activeMultiFilters.approverId) {
        payload.approverId = activeMultiFilters.approverId;
      }
      if (exportMode !== "date") {
        if (activeMultiFilters.fromDate) {
          payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
        }
        if (activeMultiFilters.toDate) {
          payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
        }
      }

      const response = await axios.post(API_URL, payload);

      const base64 = extractExcelBase64(response.data);

      if (!base64) {
        throw new Error("Invalid Excel data");
      }

      triggerXlsxDownloadFromBase64(base64, "Purchase_Orders");

      toast.success("Purchase Orders exported successfully.");

      setShowExportModal(false);
    } catch (error) {
      console.error(error);

      toast.error("Failed to export Purchase Orders.");
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = pos.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleDeleteClick = (po) => {
    const pid = getPoId(po);
    if (!pid) {
      toast.error("PO ID is missing", { autoClose: 2000 });
      return;
    }
    setDeletePoId(pid);
    setDeletePoNumber(poNumber(po));
  };

  const handleRevertClick = (po) => {
    const pid = getPoId(po);
    if (!pid) {
      toast.error("PO ID is missing", { autoClose: 2000 });
      return;
    }
    setRevertPoId(pid);
    setRevertPoNumber(poNumber(po));
  };

  const handleDownloadPoPdf = async (po) => {
    const pid = getPoId(po);
    if (!hasPoPdf(po)) {
      toast.error("PDF is not available for this PO", { autoClose: 2000 });
      return;
    }
    if (downloadingPoId) return;

    const filename = getPoPdfFilename(po);
    setDownloadingPoId(pid || getPoPdfLink(po));

    try {
      const downloadUrl = await resolvePoPdfDownloadUrl(po);

      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error("Download failed");
        }
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } catch (downloadError) {
        console.error("PO PDF blob download error:", downloadError);
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("PO PDF download error:", error);
      toast.error(error.message || "Failed to download PDF", {
        autoClose: 2000,
      });
    } finally {
      setDownloadingPoId("");
    }
  };

  const handleApproveClick = (po) => {
    const pid = getPoId(po);
    if (!pid) {
      toast.error("PO ID is missing", { autoClose: 2000 });
      return;
    }
    if (!canCurrentUserApprovePo(po)) {
      toast.error("You are not authorized to approve this PO", {
        autoClose: 2000,
      });
      return;
    }
    setApprovePoId(pid);
    setApprovePoNumber(poNumber(po));
    setPendingApproveIds([pid]);
    if (window.$) {
      window.$("#modalapprove").modal("show");
    }
  };

  const getApprovablePosOnPage = () => pos.filter(canCurrentUserApprovePo);

  const toggleSelectPo = (poId) => {
    if (!poId) return;
    setSelectedPoIds((prev) => {
      const next = new Set(prev);
      if (next.has(poId)) next.delete(poId);
      else next.add(poId);
      return next;
    });
  };

  const toggleSelectAllApprovableOnPage = () => {
    const pageIds = getApprovablePosOnPage().map(getPoId).filter(Boolean);
    if (pageIds.length === 0) return;

    const allSelected = pageIds.every((id) => selectedPoIds.has(id));
    setSelectedPoIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const isAllApprovableOnPageSelected = () => {
    const pageIds = getApprovablePosOnPage().map(getPoId).filter(Boolean);
    return pageIds.length > 0 && pageIds.every((id) => selectedPoIds.has(id));
  };

  const handleBulkApproveClick = () => {
    const ids = [...selectedPoIds];
    if (ids.length === 0) {
      toast.error("Select at least one PO to approve", { autoClose: 2000 });
      return;
    }

    setPendingApproveIds(ids);
    setApprovePoId(null);
    setApprovePoNumber(
      ids.length === 1
        ? poNumber(pos.find((po) => getPoId(po) === ids[0])) || ids[0]
        : `${ids.length} purchase orders`,
    );
    if (window.$) {
      window.$("#modalapprove").modal("show");
    }
  };

  const handleCreatePoClick = () => {
    if (window.$) {
      window.$("#modalcreatepo").modal("show");
    }
  };

  const handleConfirmCreatePo = () => {
    if (window.$) {
      window.$("#modalcreatepo").modal("hide");
    }
    navigate("/poCreate");
  };

  const handleApprovePo = async () => {
    const ids =
      pendingApproveIds.length > 0
        ? pendingApproveIds
        : approvePoId
          ? [approvePoId]
          : [];

    if (ids.length === 0) return;

    const userId = localStorage.getItem("id");
    if (!userId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsApproving(true);
    try {
      const response = await axios.post(API_URL, {
        action: "approve-po",
        userid: userId,
        poid: ids,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message ||
            (ids.length > 1
              ? `${ids.length} POs approved successfully`
              : "PO approved successfully"),
          { autoClose: 2000 },
        );
        if (window.$) {
          window.$("#modalapprove").modal("hide");
        }
        setApprovePoId(null);
        setApprovePoNumber("");
        setPendingApproveIds([]);
        setSelectedPoIds((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
        fetchPos();
        fetchPoStatusCount();
      } else {
        toast.error(responseData?.message || "Failed to approve PO", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Approve PO error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "An error occurred while approving PO", {
        autoClose: 2000,
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePoId) return;

    const requestUserId = localStorage.getItem("id");
    if (!requestUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-po",
        id: deletePoId,
        requestUserId,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || "PO deleted successfully", {
          autoClose: 2000,
        });
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }
        setDeletePoId(null);
        setDeletePoNumber("");
        if (pos.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchPos();
          fetchPoStatusCount();
        }
      } else {
        toast.error(responseData?.message || "Failed to delete PO", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete PO error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "An error occurred while deleting PO", {
        autoClose: 2000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRevert = async () => {
    if (!revertPoId) return;

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsReverting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "revert-po-linked-references",
        id: revertPoId,
        currentUserId,
        setInactive: true,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "PO linked references reverted successfully",
          { autoClose: 2000 },
        );
        if (window.$) {
          window.$("#modalrevertpo").modal("hide");
        }
        setRevertPoId(null);
        setRevertPoNumber("");
        fetchPos();
        fetchPoStatusCount();
      } else {
        toast.error(
          responseData?.message || "Failed to revert PO linked references",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Revert PO error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while reverting PO",
        { autoClose: 2000 },
      );
    } finally {
      setIsReverting(false);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  useEffect(() => {
    sessionStorage.setItem(
      "poFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        filterChecks,
        filterInputs,
        activeMultiFilters,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    filterChecks,
    filterInputs,
    activeMultiFilters,
    sortBy,
    sortOrder,
  ]);

  const hasActiveFilters = Boolean(activeSearchQuery || anyMultiFilterActive);

  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];
      const mutuallyExclusiveKey =
        key === "poStatus"
          ? "giStatus"
          : key === "giStatus"
            ? "poStatus"
            : null;

      if (!isCurrentlyChecked) {
        const activeCount = Object.keys(prev).filter(
          (k) => k !== mutuallyExclusiveKey && prev[k],
        ).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev; 
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };
      if (next[key] && mutuallyExclusiveKey) {
        next[mutuallyExclusiveKey] = false;
      }

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
      }
      if (
        mutuallyExclusiveKey &&
        !next[mutuallyExclusiveKey] &&
        prev[mutuallyExclusiveKey]
      ) {
        setFilterInputs((inputs) => ({
          ...inputs,
          [mutuallyExclusiveKey]: "",
        }));
        setActiveMultiFilters((active) => ({
          ...active,
          [mutuallyExclusiveKey]: "",
        }));
      }
      if (!next[key] || (mutuallyExclusiveKey && prev[mutuallyExclusiveKey])) {
        if (currentPage !== 1) setCurrentPage(1);
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const isPoStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeMultiFilters.poStatus && !activeMultiFilters.giStatus;
    }
    if (card.isGiStatus) {
      return activeMultiFilters.giStatus === card.filterValue;
    }
    return activeMultiFilters.poStatus === card.filterValue;
  };

  const handlePoStatCardClick = (card) => {
    if (!card.filterValue) {
      setFilterChecks((prev) => ({
        ...prev,
        poStatus: false,
        giStatus: false,
      }));
      setFilterInputs((prev) => ({ ...prev, poStatus: "", giStatus: "" }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        poStatus: "",
        giStatus: "",
      }));
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }

    const cardFilterKey = card.isGiStatus ? "giStatus" : "poStatus";
    const otherStatusKey = card.isGiStatus ? "poStatus" : "giStatus";

    if (!filterChecks[cardFilterKey]) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) =>
          key !== cardFilterKey && key !== otherStatusKey && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        const clearRemovedInputs =
          keyToRemove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [keyToRemove]: "" };
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          [otherStatusKey]: false,
          [cardFilterKey]: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          [otherStatusKey]: "",
          [cardFilterKey]: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          [otherStatusKey]: "",
          [cardFilterKey]: card.filterValue,
        }));
        toast.info("Replaced an existing filter to apply this status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({
          ...prev,
          [otherStatusKey]: false,
          [cardFilterKey]: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          [otherStatusKey]: "",
          [cardFilterKey]: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          [otherStatusKey]: "",
          [cardFilterKey]: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({
        ...prev,
        [cardFilterKey]: card.filterValue,
      }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        [cardFilterKey]: card.filterValue,
      }));
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleSort = (column) => {
    const apiField = PO_SORT_COLUMN_MAP[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy(DEFAULT_SORT_BY);
        setSortOrder(DEFAULT_SORT_ORDER);
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const getSortIcon = (column) => {
    const apiField = PO_SORT_COLUMN_MAP[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: "pointer",
        whiteSpace: "nowrap",
        ...extraStyle,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Purchase Orders</h1>
            </div>

            <div className="col-sm-6">
              <div className="d-flex justify-content-end align-items-center">
                <button
                  type="button"
                  className="btn btn-success mr-2"
                  onClick={handleBulkApproveClick}
                  disabled={isApproving || selectedPoIds.size === 0}
                >
                  {isApproving
                    ? "Approving..."
                    : selectedPoIds.size > 0
                      ? `Approve (${selectedPoIds.size})`
                      : "Approve"}
                </button>
                {canCreatePo && (
                  <button
                    type="button"
                    className="btn btn-blue mr-2"
                    onClick={handleCreatePoClick}
                  >
                    Create PO
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => setShowExportModal(true)}
                  disabled={isExporting}
                >
                  <i
                    className={`fa-solid ${
                      isExporting ? "fa-spinner fa-spin" : "fa-file-export"
                    } mr-1`}
                  />
                  {isExporting ? "Exporting..." : "Export"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isPoStatusLoading
                  ? PO_STAT_CARDS.map((card) => (
                      <PoStatCardSkeleton key={card.key} />
                    ))
                  : PO_STAT_CARDS.map((card) => {
                      const formatted = formatPoStatCardValue(
                        card,
                        poStatus[card.key] ?? 0,
                      );
                      return (
                        <PoStatCard
                          key={card.key}
                          label={card.label}
                          value={formatted.display}
                          fullValue={formatted.full}
                          titleColor={card.titleColor}
                          valueColor={card.valueColor}
                          isActive={isPoStatCardActive(card)}
                          onClick={() => handlePoStatCardClick(card)}
                        />
                      );
                    })}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search purchase orders..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Filter
                        </label>

                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() => setIsFilterPanelOpen((open) => !open)}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {anyMultiFilterActive
                              ? "Filters"
                              : "Select Filters"}
                          </span>

                          <i
                            className={`fa-solid fa-chevron-${
                              isFilterPanelOpen ? "up" : "down"
                            }`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          />
                        </button>

                        {isFilterPanelOpen && (
                          <div
                            className="po-filter-dropdown"
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "200px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            {[
                              {
                                key: "poStatus",
                                id: "filterCheckPoStatus",
                                label: "PO Status",
                              },
                              {
                                key: "giStatus",
                                id: "filterCheckGiStatus",
                                label: "GI Status",
                              },
                              {
                                key: "createdBy",
                                id: "filterCheckCreatedBy",
                                label: "Created User",
                              },
                              {
                                key: "dateRange",
                                id: "filterCheckDateRange",
                                label: "Date",
                              },
                            ].map((filter) => {
                              const activeCount =
                                Object.values(filterChecks).filter(
                                  Boolean,
                                ).length;

                              return (
                                <div
                                  className="custom-control custom-checkbox mb-2"
                                  key={filter.key}
                                >
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={filter.id}
                                    checked={filterChecks[filter.key]}
                                    disabled={
                                      !filterChecks[filter.key] &&
                                      activeCount >= MAX_ACTIVE_FILTERS
                                    }
                                    onChange={() =>
                                      handleFilterCheckToggle(filter.key)
                                    }
                                  />

                                  <label
                                    className="custom-control-label"
                                    htmlFor={filter.id}
                                  >
                                    {filter.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {filterChecks.poStatus && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            PO Status
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.poStatus}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "poStatus",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select PO Status</option>

                            {PO_STATUS_OPTIONS.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.giStatus && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            GI Status
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.giStatus}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "giStatus",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select GI Status</option>

                            {GI_STATUS_OPTIONS.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.createdBy && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Created User
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.createdBy}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "createdBy",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isPoUsersLoading && poUsers.length === 0
                                ? "Loading users..."
                                : "Select User"}
                            </option>

                            {poUsers.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.dateRange && (
                      <>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              From Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.fromDate || ""}
                              max={filterInputs.toDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "fromDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              To Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.toDate || ""}
                              min={filterInputs.fromDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "toDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {anyMultiFilterActive && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearFilter}
                          style={{
                            minWidth: "100px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : pos.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no purchase orders yet.
                  <br />
                  {canCreatePo && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleCreatePoClick}
                      >
                        Create PO
                      </button>
                    </div>
                  )}
                </div>
              ) : pos.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No purchase orders found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="purchaseOrders"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              minWidth: "36px",
                              width: "36px",
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isAllApprovableOnPageSelected()}
                              onChange={toggleSelectAllApprovableOnPage}
                              disabled={getApprovablePosOnPage().length === 0}
                              title="Select all POs awaiting your approval"
                            />
                          </th>
                          <th
                            style={{ whiteSpace: "nowrap", minWidth: "35px" }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("poNumber", "PO No", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader("poDate", "PO Date", {
                            minWidth: "80px",
                          })}
                          {renderSortableHeader(
                            "poDeliveryDate",
                            "Delivery Date",
                            { minWidth: "100px" },
                          )}
                          <th style={{ minWidth: "180px" }}>Summary</th>
                          <th style={{ minWidth: "160px" }}>Supplier Name</th>
                          <th style={{ minWidth: "110px" }}>Basic Price</th>
                          {renderSortableHeader("poStatus", "PO Status", {
                            minWidth: "130px",
                          })}
                          <th style={{ minWidth: "120px" }}>Approver</th>
                          <th
                            style={{
                              minWidth: "55px",
                              width: "55px",
                              textAlign: "center",
                            }}
                          >
                            Approved
                          </th>
                          {renderSortableHeader("createdBy", "Created User", {
                            minWidth: "130px",
                          })}
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pos.map((po, index) => {
                          const pid = getPoId(po);
                          const canApprove = canCurrentUserApprovePo(po);
                          const isSelected = Boolean(
                            pid && selectedPoIds.has(pid),
                          );
                          const supplierNameLabel = poSupplierNameValue(po);
                          return (
                            <tr key={pid || index}>
                              <td className="text-center">
                                {canApprove ? (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSelectPo(pid)}
                                    title="Select to approve"
                                  />
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {poNumber(po)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatPoDate(po.poDate)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatPoDate(po.poDeliveryDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "230px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {poSummaryValue(po)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {supplierNameLabel}
                              </td>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {renderPoGrossBasicPrice(po)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {getPoDisplayStatus(po)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {po.approverName || "-"}
                              </td>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  textAlign: "center",
                                }}
                              >
                                {renderApprovedToggleCell(po, {
                                  onApprove: () => handleApproveClick(po),
                                  isApproving,
                                  approvePoId,
                                })}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {poCreatedByName(po)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canViewPo && (
                                    <Link
                                      to={`/poView/${pid}`}
                                      className="crsr_pntr mr-2"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </Link>
                                  )}
                                  {hasPoPdf(po) ? (
                                    <button
                                      type="button"
                                      className="btn btn-link crsr_pntr mr-2 p-0 border-0"
                                      title="Download PDF"
                                      onClick={() => handleDownloadPoPdf(po)}
                                      disabled={downloadingPoId === pid}
                                    >
                                      <i
                                        className={
                                          downloadingPoId === pid
                                            ? "fa-solid fa-spinner fa-spin"
                                            : "fa-solid fa-download"
                                        }
                                      />
                                    </button>
                                  ) : null}
                                  {canUpdatePo && (
                                    <Link
                                      to={`/poEdit/${pid}`}
                                      className="crsr_pntr mr-2"
                                      title="Edit"
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </Link>
                                  )}
                                  {canUpdatePo && (
                                    <a
                                      href="#modalrevertpo"
                                      className="crsr_pntr action-icon-link mr-2"
                                      data-toggle="modal"
                                      data-target="#modalrevertpo"
                                      title="Revert"
                                      onClick={() => handleRevertClick(po)}
                                    >
                                      <i className="fa-solid fa-rotate-left" />
                                    </a>
                                  )}
                                  {canDeletePo && (
                                    <a
                                      href="#modaldelete"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldelete"
                                      title="Delete"
                                      onClick={() => handleDeleteClick(po)}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && pos.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>
                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>
                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>
                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${currentPage === page ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}
                            <li
                              className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete PO
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">{deletePoNumber || "-"}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalrevertpo"
        tabIndex={-1}
        aria-labelledby="modalrevertpoLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalrevertpoLabel">
                Revert PO
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isReverting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to revert linked references for
                <br />
                <span className="fwt_600">{revertPoNumber || "-"}</span>?
                <br />
                <small className="text-muted">
                  This will set the PO to inactive.
                </small>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isReverting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleRevert}
                disabled={isReverting}
              >
                {isReverting ? "Reverting..." : "Yes, revert"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalcreatepo"
        tabIndex={-1}
        aria-labelledby="modalcreatepoLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalcreatepoLabel">
                Create PO
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to create PO?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                No
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleConfirmCreatePo}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalapprove"
        tabIndex={-1}
        aria-labelledby="modalapproveLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalapproveLabel">
                Approve PO
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isApproving}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                {pendingApproveIds.length > 1 ? (
                  <>
                    Are you sure you want to approve
                    <br />
                    <span className="fwt_600">
                      {pendingApproveIds.length} purchase orders
                    </span>
                    ?
                  </>
                ) : (
                  <>
                    Are you sure you want to approve this PO
                    <br />
                    <span className="fwt_600">{approvePoNumber || "-"}</span>?
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isApproving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleApprovePo}
                disabled={isApproving}
              >
                {isApproving ? "Approving..." : "Yes, approve"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showExportModal ? "show" : ""}`}
        style={{ display: showExportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalExportPoLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false);
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportPoLabel">
                Export Purchase Orders
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => {
                  if (!isExporting) setShowExportModal(false);
                }}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {hasActiveFilters && (
                <p className="text-muted small mb-3">
                  Current filters will be included:
                  {activeSearchQuery && (
                    <span className="d-block">Search: {activeSearchQuery}</span>
                  )}
                  {activeMultiFilters.poStatus && (
                    <span className="d-block">
                      PO Status: {activeMultiFilters.poStatus}
                    </span>
                  )}
                  {activeMultiFilters.giStatus && (
                    <span className="d-block">
                      GI Status: {activeMultiFilters.giStatus}
                    </span>
                  )}
                  {activeMultiFilters.createdBy && (
                    <span className="d-block">Created User selected</span>
                  )}
                  {activeMultiFilters.approverId && (
                    <span className="d-block">Approver selected</span>
                  )}
                  {(activeMultiFilters.fromDate ||
                    activeMultiFilters.toDate) && (
                    <span className="d-block">
                      Date:{" "}
                      {activeMultiFilters.fromDate
                        ? toApiDDMMYYYY(activeMultiFilters.fromDate)
                        : "-"}{" "}
                      to{" "}
                      {activeMultiFilters.toDate
                        ? toApiDDMMYYYY(activeMultiFilters.toDate)
                        : "-"}
                    </span>
                  )}
                </p>
              )}
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllPo"
                    name="exportTypePo"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === "all"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label className="custom-control-label" htmlFor="exportAllPo">
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDatePo"
                    name="exportTypePo"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === "date"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportDatePo"
                  >
                    Filter by date
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    max={exportToDate || undefined}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    min={exportFromDate || undefined}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleExportPo}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default Po;